/**
 * ColorPicker - Hue wheel color picker using @uiw/react-color-wheel
 */

import Wheel from '@uiw/react-color-wheel';
import { useCallback, useState, useEffect } from 'react';

import { cn } from '../utils';

export interface RGBColor {
  red: number;
  green: number;
  blue: number;
}

export interface ColorPickerProps {
  /** Label for the picker */
  label?: string;
  /** Current color value */
  value?: RGBColor | null;
  /** Change handler */
  onChange?: (color: RGBColor) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Size of the color picker in pixels */
  size?: number;
  /** Additional class names */
  className?: string;
}

// Convert RGB to HSV
function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (max !== min) {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }

  return { h, s: s * 100, v: v * 100 };
}

export function ColorPicker({
  label,
  value,
  onChange,
  disabled,
  size = 160,
  className,
}: ColorPickerProps) {
  const [hsva, setHsva] = useState(() => {
    if (value) {
      const hsv = rgbToHsv(value.red, value.green, value.blue);
      return { ...hsv, a: 1 };
    }
    return { h: 0, s: 100, v: 100, a: 1 };
  });

  // Sync external value changes
  useEffect(() => {
    if (value) {
      const newHsv = rgbToHsv(value.red, value.green, value.blue);
      if (
        Math.abs(newHsv.h - hsva.h) > 2 ||
        Math.abs(newHsv.s - hsva.s) > 2 ||
        Math.abs(newHsv.v - hsva.v) > 2
      ) {
        setHsva({ ...newHsv, a: 1 });
      }
    }
  }, [value, hsva]);

  const handleChange = useCallback(
    (color: {
      hsva: { h: number; s: number; v: number; a: number };
      rgb: { r: number; g: number; b: number };
    }) => {
      if (disabled) return;
      setHsva(color.hsva);
      onChange?.({
        red: color.rgb.r,
        green: color.rgb.g,
        blue: color.rgb.b,
      });
    },
    [disabled, onChange]
  );

  const currentColorCss = value ? `rgb(${value.red}, ${value.green}, ${value.blue})` : '#ff0000';

  return (
    <div className={className}>
      {label && (
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          {label}
        </label>
      )}

      <div className="flex items-center gap-4">
        <div className={cn(disabled && 'pointer-events-none opacity-50')}>
          <Wheel color={hsva} onChange={handleChange} width={size} height={size} />
        </div>

        {/* Current color preview */}
        <div className="flex flex-col items-center gap-1">
          <div
            className="h-10 w-10 rounded-lg border border-gray-200 shadow-inner dark:border-zinc-700"
            style={{ backgroundColor: currentColorCss }}
          />
          <span className="text-xs text-gray-500 dark:text-zinc-400">Current</span>
        </div>
      </div>
    </div>
  );
}
